// INV-1: keyboard occlusion (inset + keyboardOpen) is derived inside this platform hook; consumers never read DOM/viewport globals to decide it.
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import type { ViewportInset } from './use-viewport-inset.types'
export type { ViewportInset } from './use-viewport-inset.types'

export function useViewportInset(onResize?: () => void): ViewportInset {
  const [inset, setInset] = useState(0)
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const baseline = useRef<{ width: number; height: number } | null>(null)
  const notify = useEffectEvent(() => onResize?.())
  useEffect(() => {
    const viewport = window.visualViewport
    const sync = () => {
      setInset(viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0)
      // resizes-content shrinks innerHeight too; preserve the unobstructed height.
      const previous = baseline.current
      const unobstructed = !previous || previous.width !== window.innerWidth
        ? { width: window.innerWidth, height: window.innerHeight }
        : { width: previous.width, height: Math.max(previous.height, window.innerHeight) }
      baseline.current = unobstructed
      // Browser chrome causes small resizes; pinch zoom also shrinks the viewport.
      const focused = document.activeElement
      const editing = focused instanceof HTMLElement &&
        (focused.matches('input, textarea') || focused.isContentEditable)
      setKeyboardOpen(editing && (!viewport || viewport.scale === 1) &&
        unobstructed.height - (viewport?.height ?? window.innerHeight) > 150)
      notify()
    }
    sync()
    window.addEventListener('resize', sync)
    document.addEventListener('focusin', sync)
    document.addEventListener('focusout', sync)
    viewport?.addEventListener('resize', sync)
    return () => {
      window.removeEventListener('resize', sync)
      document.removeEventListener('focusin', sync)
      document.removeEventListener('focusout', sync)
      viewport?.removeEventListener('resize', sync)
    }
  }, [])
  return { inset, keyboardOpen }
}
